import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import Icon from '../components/Icon';
import { useLocale } from '../utils/locale';

function ExerciseRow({ ex, onChange, onRemove, exercises }) {
  return (
    <div style={{ background: 'var(--color-surface-2)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <select className="form-select" style={{ flex: 1, marginRight: 8 }} value={ex.exercise_id || ''}
          onChange={e => onChange({ ...ex, exercise_id: parseInt(e.target.value) })}>
          <option value="">Select exercise</option>
          {exercises.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <button className="icon-btn" onClick={onRemove}>
          <Icon name="icon-delete.png" alt="Remove" />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Sets</div>
          <input className="form-input" type="number" min="1" placeholder="3" value={ex.sets || ''}
            onChange={e => onChange({ ...ex, sets: e.target.value })} style={{ minHeight: 40, fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Reps</div>
          <input className="form-input" placeholder="10" value={ex.reps || ''}
            onChange={e => onChange({ ...ex, reps: e.target.value })} style={{ minHeight: 40, fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="icon-clock.png" size={11} />
            Rest (s)
          </div>
          <input className="form-input" type="number" placeholder="60" value={ex.rest_seconds || ''}
            onChange={e => onChange({ ...ex, rest_seconds: e.target.value })} style={{ minHeight: 40, fontSize: 14 }} />
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <input className="form-input" placeholder="Notes (optional)" value={ex.notes || ''}
          onChange={e => onChange({ ...ex, notes: e.target.value })} style={{ minHeight: 40, fontSize: 14 }} />
      </div>
    </div>
  );
}

export default function ProgramBuilder() {
  const { t } = useLocale();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== 'new';

  const [exercises, setExercises] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: '', goal: '', duration_weeks: '', sessions_per_week: '' });
  const [sessions, setSessions] = useState([{ day_number: 1, session_name: 'Day 1', exercises: [] }]);
  const [saving, setSaving] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignClientId, setAssignClientId] = useState('');
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assigned, setAssigned] = useState(false);

  useEffect(() => {
    api.get('/exercises').then(setExercises);
    api.get('/clients').then(setClients);
    if (isEdit) {
      api.get('/programs/' + id).then(data => {
        setForm({ name: data.name, goal: data.goal||'', duration_weeks: data.duration_weeks||'', sessions_per_week: data.sessions_per_week||'' });
        setSessions(data.sessions.map(s => ({
          day_number: s.day_number,
          session_name: s.session_name || 'Day ' + s.day_number,
          exercises: s.exercises.map(e => ({ exercise_id: e.exercise_id, sets: e.sets, reps: e.reps, rest_seconds: e.rest_seconds, notes: e.notes||'' }))
        })));
      });
    }
  }, [id, isEdit]);

  function addSession() {
    const next = sessions.length + 1;
    setSessions(prev => [...prev, { day_number: next, session_name: 'Day ' + next, exercises: [] }]);
  }

  function removeSession(idx) {
    setSessions(prev => prev.filter((_, i) => i !== idx));
  }

  function addExercise(sessionIdx) {
    setSessions(prev => prev.map((s, i) => i === sessionIdx
      ? { ...s, exercises: [...s.exercises, { exercise_id: '', sets: '', reps: '', rest_seconds: '', notes: '' }] }
      : s
    ));
  }

  function updateExercise(sessionIdx, exIdx, updated) {
    setSessions(prev => prev.map((s, i) => i === sessionIdx
      ? { ...s, exercises: s.exercises.map((e, j) => j === exIdx ? updated : e) }
      : s
    ));
  }

  function removeExercise(sessionIdx, exIdx) {
    setSessions(prev => prev.map((s, i) => i === sessionIdx
      ? { ...s, exercises: s.exercises.filter((_, j) => j !== exIdx) }
      : s
    ));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, sessions };
      if (isEdit) {
        await api.put('/programs/' + id, payload);
        navigate('/programs/' + id);
      } else {
        const created = await api.post('/programs', form);
        await api.put('/programs/' + created.id, { ...form, sessions });
        navigate('/programs/' + created.id);
      }
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  async function handleAssign(e) {
    e.preventDefault();
    try {
      await api.post('/programs/' + id + '/assign', { client_id: parseInt(assignClientId), start_date: assignStartDate || null });
      setAssigned(true);
      setShowAssign(false);
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/programs')}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1 }}>{isEdit ? t('Edit Program') : t('New Program')}</h1>
        {isEdit && (
          <button className="icon-btn" onClick={() => setShowAssign(true)} title="Assign to client">
            <Icon name="icon-user.png" alt="Assign" />
          </button>
        )}
      </div>

      <div className="page-content">
        {assigned && <div className="success-msg">{t('Program assigned successfully!')}</div>}

        {showAssign && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Assign to Client</div>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-select" value={assignClientId} onChange={e => setAssignClientId(e.target.value)} required>
                  <option value="">Select client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date (optional)</label>
                <input className="form-input" type="date" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>Assign</button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setShowAssign(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('Program Details')}</div>
            <div className="form-group">
              <label className="form-label">{t('Program Name *')}</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Goal</label>
              <input className="form-input" value={form.goal} onChange={e => setForm(f => ({...f, goal: e.target.value}))} placeholder="e.g. Build strength, lose weight" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <div className="form-group">
                <label className="form-label">Duration (weeks)</label>
                <input className="form-input" type="number" min="1" value={form.duration_weeks} onChange={e => setForm(f => ({...f, duration_weeks: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Sessions / week</label>
                <input className="form-input" type="number" min="1" max="7" value={form.sessions_per_week} onChange={e => setForm(f => ({...f, sessions_per_week: e.target.value}))} />
              </div>
            </div>
          </div>

          {sessions.map((session, si) => (
            <div key={si} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <input
                  className="form-input"
                  value={session.session_name}
                  onChange={e => setSessions(prev => prev.map((s, i) => i === si ? { ...s, session_name: e.target.value } : s))}
                  style={{ fontWeight: 600, flex: 1, marginRight: 8 }}
                />
                <button type="button" className="icon-btn" onClick={() => removeSession(si)}>
                  <Icon name="icon-delete.png" alt="Remove" />
                </button>
              </div>

              {session.exercises.map((ex, ei) => (
                <ExerciseRow key={ei} ex={ex} exercises={exercises}
                  onChange={updated => updateExercise(si, ei, updated)}
                  onRemove={() => removeExercise(si, ei)} />
              ))}

              <button type="button" className="btn btn-ghost" style={{ width: '100%', marginTop: 4 }}
                onClick={() => addExercise(si)}>
                <Icon name="icon-add.png" size={16} />
                Add Exercise
              </button>
            </div>
          ))}

          <button type="button" className="btn btn-ghost" style={{ width: '100%', marginBottom: 12 }} onClick={addSession}>
            <Icon name="icon-add.png" size={16} />
            Add Session
          </button>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : t('Save Program')}
          </button>
        </form>
      </div>
    </div>
  );
}
